import { useId, useState } from 'react';
import {
  Box,
  Button,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  BoltIcon,
  CheckIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

import { primitiveColors, semanticColors, BORDER_RADIUS } from '@/theme/tokens';

import { ANSWER_DEPTHS, AnswerDepth, depthHintKey, depthLabelKey } from './AnswerDepth';

/**
 * A glyph per option, so the choice is legible before the words are read.
 *
 * The words alone asked a lot of a voter reading below high-school level: three
 * abstract nouns with no picture to hang them on. A bolt for "right away", a
 * magnifier for "looks things up", sparkles for "VoteMate picks" carry the gist
 * on their own, and the sentence underneath confirms it rather than being the
 * only way in.
 */
const DEPTH_ICONS: Record<AnswerDepth, typeof BoltIcon> = {
  auto: SparklesIcon,
  quick: BoltIcon,
  deep: MagnifyingGlassIcon,
};

/**
 * Chooses how VoteMate answers the next question.
 *
 * The menu leads with the question it answers ("How should VoteMate answer?"),
 * then gives each option a glyph, a plain one-word name, and one short
 * sentence — a list of three bare nouns under no heading left a voter to infer
 * what the setting even was.
 *
 * Sits beside the send button rather than above the input: `auto` is the right
 * choice almost always, so the control earns an icon rather than permanent
 * vertical space in the chat column. The trigger is the chosen option's own
 * glyph plus a chevron — sparkles, bolt, or magnifier, the same mark that
 * option carries in the menu. A lone word ("Normal") named a setting without
 * saying it could be changed; the glyph says "options here" and, being the
 * menu's own mark, teaches which one is on. It stays caption grey in every
 * state — the shape carries the state, so a tint change would only make the
 * composer louder. The depth sticks for the rest of the session — someone
 * comparing several candidates in depth should not have to re-pick per message
 * — and resets to `auto` on reload, so a forgotten `deep` cannot quietly slow
 * every future answer.
 */

interface AnswerDepthSelectorProps {
  depth: AnswerDepth;
  onChange: (depth: AnswerDepth) => void;
  disabled?: boolean;
}

export default function AnswerDepthSelector({
  depth,
  onChange,
  disabled,
}: AnswerDepthSelectorProps) {
  const { t } = useTranslation('chat');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const menuId = useId();
  const titleId = useId();
  const open = Boolean(anchorEl);
  const TriggerIcon = DEPTH_ICONS[depth];

  const handleSelect = (next: AnswerDepth) => {
    setAnchorEl(null);
    // Re-picking the current depth is a no-op rather than an event: the
    // analytics question is which depths voters move to, and a confirm-the-
    // status-quo tap would inflate that with non-changes.
    if (next !== depth) onChange(next);
  };

  return (
    <>
      <Tooltip title={t('input.depth.tooltip')}>
        {/* span keeps the tooltip working while the button is disabled */}
        <span>
          <Button
            onClick={(event) => setAnchorEl(event.currentTarget)}
            disabled={disabled}
            size="small"
            aria-label={t('input.depth.ariaLabel', { depth: t(depthLabelKey(depth)) })}
            aria-haspopup="menu"
            aria-expanded={open}
            aria-controls={open ? menuId : undefined}
            startIcon={<TriggerIcon style={{ width: 18, height: 18 }} aria-hidden="true" />}
            endIcon={<ChevronDownIcon style={{ width: 14, height: 14 }} aria-hidden="true" />}
            // A soft pill, sized to the send button beside it: the fill says
            // "this is a control" without the visual weight of an outline.
            sx={{
              minWidth: 0,
              height: 36,
              minHeight: 36,
              // Shorthand on purpose — the global MuiButton override sets a
              // shorthand ``padding`` that would otherwise win the tie.
              padding: '0 10px',
              borderRadius: '999px',
              textTransform: 'none',
              backgroundColor: semanticColors.surfaceWarm,
              '& .MuiButton-startIcon': { mr: 0, ml: 0 },
              '& .MuiButton-endIcon': { ml: '2px' },
              // Both glyphs inherit this. One caption grey in every state: the
              // glyph's own shape says which setting is on, so a second signal
              // in the brand tint only made the composer louder.
              color: semanticColors.textCaption,
              transition: 'background-color 0.2s ease, color 0.2s ease',
              '&:hover': {
                backgroundColor: primitiveColors.gray3,
                color: semanticColors.textBody,
              },
              '&.Mui-disabled': {
                backgroundColor: semanticColors.surfaceWarm,
                color: semanticColors.disabled,
              },
              '&:focus-visible': {
                outline: `2px solid ${semanticColors.brandPrimary}`,
                outlineOffset: '2px',
              },
            }}
          />
        </span>
      </Tooltip>

      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        slotProps={{
          paper: { sx: { borderRadius: BORDER_RADIUS, maxWidth: 320 } },
          list: { 'aria-labelledby': titleId, sx: { pt: 0 } },
        }}
      >
        <ListSubheader
          id={titleId}
          // `presentation`, so the heading is not exposed as a fourth child of
          // `role="menu"` — it names the menu through `aria-labelledby`
          // instead, which reads it whether or not it is in the item list.
          role="presentation"
          disableSticky
          sx={{
            pt: 1.25,
            pb: 0.75,
            fontSize: 14,
            fontWeight: 600,
            lineHeight: 1.35,
            whiteSpace: 'normal',
            color: semanticColors.textPrimary,
          }}
        >
          {t('input.depth.tooltip')}
        </ListSubheader>
        {ANSWER_DEPTHS.map((option) => {
          const selected = option === depth;
          const Icon = DEPTH_ICONS[option];
          return (
            <MenuItem
              key={option}
              // radio semantics, not a plain menu item: these are three states of
              // one setting, and a screen reader should say which one is active.
              role="menuitemradio"
              aria-checked={selected}
              selected={selected}
              onClick={() => handleSelect(option)}
              sx={{ alignItems: 'center', gap: 1.5, py: 1 }}
            >
              <ListItemIcon sx={{ minWidth: 0 }}>
                <Icon
                  aria-hidden="true"
                  style={{
                    width: 20,
                    height: 20,
                    color: selected ? semanticColors.textBrand : semanticColors.textCaption,
                  }}
                />
              </ListItemIcon>
              <ListItemText
                primary={t(depthLabelKey(option))}
                secondary={t(depthHintKey(option))}
                slotProps={{
                  primary: {
                    sx: {
                      fontSize: 14,
                      fontWeight: 600,
                      color: selected ? semanticColors.textBrand : semanticColors.textPrimary,
                    },
                  },
                  // `whiteSpace: normal` because a MenuItem sets `nowrap`, and
                  // a one-sentence hint is the point of the row.
                  secondary: { sx: { fontSize: 12, lineHeight: 1.4, whiteSpace: 'normal' } },
                }}
              />
              {/* A check, not only the selected row's tint: MUI's `selected`
                  background is the same tint as hover, so on a pointer device
                  the active option was indistinguishable from the one under the
                  cursor. The space is reserved either way, so picking a
                  different option moves nothing but the check. */}
              <Box sx={{ width: 18, flex: '0 0 auto', display: 'flex' }}>
                {selected && (
                  <CheckIcon
                    aria-hidden="true"
                    style={{ width: 18, height: 18, color: semanticColors.textBrand }}
                  />
                )}
              </Box>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
